import { Building2, Users } from "lucide-react";
import { PortalLayout } from "../components/PortalLayout";
import { platformLogoMap } from "../config/platforms";

const ownedPlatforms = [
  { name: "AutoGuard", logoKey: "AutoGuard", category: "Security Operations" },
  { name: "MiCash", logoKey: "MiCash", category: "Digital Payments" },
  { name: "Life Happens", logoKey: "Life Happens", category: "Life-Stage Services" },
  { name: "Get Me Printing", logoKey: "Get Me Printing", category: "Print Services" },
];

const externalPartners = [
  { name: "Lion Software Development", role: "Software development partner", description: "Specialised software engineering and technical development execution." },
  { name: "Beyond Imagination", role: "Marketing partner", description: "Brand strategy, marketing execution, and communications support." },
  { name: "Franchise Tech Solutions", role: "Design and implementation partner", description: "UX/UI design, technical implementation, and deployment services." },
  { name: "Unlimited Technologies", role: "Network and infrastructure support provider", description: "Network infrastructure, connectivity, and technical support services." },
];

export function OperatingModelPage() {
  return (
    <PortalLayout>

      {/* Header */}
      <div className="mb-14">
        <p className="text-xs font-semibold uppercase tracking-widest text-[#7B1A1A] mb-4">
          Operating Model & Group Structure
        </p>
        <h1 className="text-2xl sm:text-4xl md:text-5xl font-semibold text-[#1F2933] mb-8 leading-tight max-w-3xl">
          Focused Ownership. Strategic Partnerships.
        </h1>
        <div className="space-y-4 text-[#4B5563] max-w-4xl leading-relaxed">
          <p>
            LEO Group operates as a centralised ownership and strategic entity, maintaining majority or full ownership
            across its core platforms while leveraging a network of strategically aligned partner entities. These
            partner entities, which may be partially or fully owned, operate independently and provide specialised
            services across development, marketing, implementation, and infrastructure support.
          </p>
          <p>
            This model allows LEO Group to maintain control over intellectual property and strategic direction, scale
            efficiently across multiple sectors, leverage specialised expertise without operational bottlenecks, and
            ensure consistent delivery across all platforms and services.
          </p>
        </div>
      </div>

      {/* Two-column structure */}
      <div className="grid md:grid-cols-2 gap-8 mb-14">

        {/* Owned Platforms */}
        <div>
          <div className="bg-[#7B1A1A] text-white p-6 rounded-t-lg flex items-center gap-3">
            <Building2 className="w-5 h-5" />
            <div>
              <h2 className="text-base font-semibold">LEO Group Holding Company</h2>
              <p className="text-xs text-white/60">Direct Ownership & Control</p>
            </div>
          </div>
          <div className="border border-[#7B1A1A]/20 border-t-0 rounded-b-lg p-6 bg-white space-y-3">
            <h3 className="text-sm font-semibold text-[#1F2933] mb-4">Core Platform Portfolio</h3>
            {ownedPlatforms.map((platform) => {
              const logoSrc = platform.logoKey ? platformLogoMap[platform.logoKey] : null;
              return (
                <div key={platform.name} className="bg-[#F8F6F4] p-4 rounded border-l-4 border-[#7B1A1A] flex items-center gap-4">
                  <div className="flex items-center justify-center h-24 flex-shrink-0">
                    <img
                      src={logoSrc ?? "/logo.png"}
                      alt={platform.name}
                      className={`${platform.name === "Life Happens" ? "max-h-14" : "max-h-16"} w-auto object-contain`}
                      onError={(e) => { e.currentTarget.src = "/logo.png"; }}
                    />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-[#1F2933]">{platform.name}</h4>
                    <p className="text-xs text-[#4B5563]">{platform.category}</p>
                  </div>
                </div>
              );
            })}
            <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
              <p className="text-xs text-[#4B5563] leading-relaxed">
                <span className="font-semibold text-[#1F2933]">Full Control:</span> Intellectual property, platform
                infrastructure, data environments, strategic direction, and commercial rights.
              </p>
            </div>
          </div>
        </div>

        {/* External Partners */}
        <div>
          <div className="bg-[#1F2933] text-white p-6 rounded-t-lg flex items-center gap-3">
            <Users className="w-5 h-5" />
            <div>
              <h2 className="text-base font-semibold">External Partner Ecosystem</h2>
              <p className="text-xs text-white/50">Independent Specialised Partners</p>
            </div>
          </div>
          <div className="border border-[#1F2933]/20 border-t-0 rounded-b-lg p-6 bg-white space-y-3">
            <h3 className="text-sm font-semibold text-[#1F2933] mb-4">Strategic Service Partners</h3>
            {externalPartners.map((partner) => (
              <div key={partner.name} className="bg-[#F8F6F4] p-4 rounded border-l-4 border-[#4B5563]">
                <h4 className="text-sm font-semibold text-[#1F2933]">{partner.name}</h4>
                <p className="text-xs text-[#7B1A1A] mb-1">{partner.role}</p>
                <p className="text-xs text-[#4B5563]">{partner.description}</p>
              </div>
            ))}
            <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
              <p className="text-xs text-[#4B5563] leading-relaxed">
                <span className="font-semibold text-[#1F2933]">Service Relationship:</span> Partners provide
                specialised execution support under defined commercial agreements. LEO Group retains full ownership
                of all outputs, intellectual property, and strategic control.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Ownership Structure */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 shadow-sm mb-14">
        <h3 className="text-base font-semibold text-[#1F2933] mb-6">Clear Ownership Structure</h3>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1 h-1 rounded-full bg-[#7B1A1A] inline-block" />
              <h4 className="text-sm font-semibold text-[#1F2933]">LEO Group Owns</h4>
            </div>
            <ul className="text-sm text-[#4B5563] space-y-1.5">
              {["All platform intellectual property", "Core infrastructure and hosting environments", "Client relationships and commercial agreements", "Strategic direction and governance"].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#7B1A1A] inline-block flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1 h-1 rounded-full bg-[#4B5563] inline-block" />
              <h4 className="text-sm font-semibold text-[#1F2933]">Partners Provide</h4>
            </div>
            <ul className="text-sm text-[#4B5563] space-y-1.5">
              {["Specialised technical execution", "Marketing and design services", "Infrastructure support services", "Subject to commercial service agreements"].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#4B5563] inline-block flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>


    </PortalLayout>
  );
}
